
/* ============================== src/components/Cabinet.jsx ============================== */
// path: frontend/src/components/Cabinet.jsx
/**
 * Личный кабинет. RBAC:
 *   - capabilities.manageProductionCalendar — вкладка «Правка календаря»;
 *   - capabilities.manageWeeklyReminders — блок «Напоминания о часах»;
 *   - delegation.permissions.canManage — «Отозвать» в списке замещений.
 *
 * Видимость строки в «Передача ролей» — тоже delegation.permissions
 * (canManage || canDecide): см. myDeleg.
 *
 * useVacationsQuery/useRoleDelegationsQuery возвращают либо массив,
 * либо конверт { items, meta } — нормализуем через unwrapItems
 * (utils/dbShape.js), как ModalRenderer. useMemo по референсу
 * стабилен за счёт EMPTY_ARRAY.
 */
import { useState, useMemo, useRef, Fragment, useEffect } from "react";
import {
  TASK_STATUSES,
  TASK_STATUS_ORDER,
  VACATION_TYPES,
  DIALOGS,
  TOASTS,
  WEEKLY_REMINDER_PERIOD_LIST,
  WEEKLY_REMINDER_OVERWORK_CHIP,
  WEEKLY_REMINDER_OVERWORK_HINT,
  WEEKLY_REMINDER_OVERWORK_LEGEND,
} from "../utils/constants";
import { TODAY, fmtDMY, fmtD, iso, addDays } from "../utils/date";
import { useDataHelpers } from "../hooks";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../context/ToastContext";
import { useConfirm } from "../context/ConfirmContext";
import { resolveReminderPeriod } from "../utils/period";
import { downloadCsv } from "../utils/csvExport";
import { buildEmployeePositions, formatPositionLabel } from "../utils/helpers";
import { unwrapItems } from "../utils/dbShape";
import { Ic } from "./Icons";
import { ICONS } from "./iconsData";
import { Modal } from "./Modal";
import Select from "./Select";
import { Clickable } from "./Clickable";
import { ColorDot } from "./ColoredSpan";
import ProductionCalendarView from "./views/ProductionCalendarView";
import { useCommitTimeLog } from "../hooks/useCommitTimeLog";
import DailyNormPraiseModal from "./Modals/DailyNormPraiseModal";
import { ChangePasswordModal } from "./Modals/ChangePasswordModal";
import Avatar from "./Avatar";
import {
  isLogBlockedByTaskStatus,
  STATUS_NEW_LOG_MESSAGE,
} from "../utils/taskTimeLog";
import {
  useUpdateSelfMutation,
  useUploadAvatarMutation,
  useDeleteAvatarMutation,
} from "../api/queries/employees";
import {
  useDeleteVacationMutation,
  useVacationsQuery,
} from "../api/queries/vacations";
import {
  useRevokeRoleDelegationMutation,
  useRoleDelegationsQuery,
} from "../api/queries/role-delegations";
import {
  useWeeklyReminderSettingsQuery,
  useUpdateWeeklyReminderSettingsMutation,
  useSendWeeklyRemindersMutation,
  usePreviewWeeklyRemindersMutation,
} from "../api/queries/weekly-reminders";

const WR_DEFAULTS = Object.freeze({
  enabled: true,
  sendHour: 14,
  sendWeekday: 5,
});

const normalizeWrDraft = (s) => ({
  enabled: s?.enabled ?? WR_DEFAULTS.enabled,
  sendHour: s?.sendHour ?? WR_DEFAULTS.sendHour,
  sendWeekday: s?.sendWeekday ?? WR_DEFAULTS.sendWeekday,
});

const WEEKDAY_OPTIONS = [
  { value: 1, label: "Понедельник" },
  { value: 2, label: "Вторник" },
  { value: 3, label: "Среда" },
  { value: 4, label: "Четверг" },
  { value: 5, label: "Пятница" },
  { value: 6, label: "Суббота" },
  { value: 7, label: "Воскресенье" },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: String(i).padStart(2, "0"),
}));

const formatDeviation = (d) => {
  if (d > 0) return `+${d}`;
  if (d < 0) return `−${Math.abs(d)}`;
  return "0";
};

function WeeklyReminderPreviewModal({ period, from, to, onClose }) {
  const { showToast } = useToast();
  const { mutateAsync: fetchPreview } = usePreviewWeeklyRemindersMutation();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    fetchPreview({ period, from, to })
      .then((data) => {
        if (isMounted) setReminders(data);
      })
      .catch((err) => {
        if (isMounted)
          showToast(err.message || "Ошибка предпросмотра", "error");
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [period, from, to, fetchPreview, showToast]);

  const [sortField, setSortField] = useState("deviation");
  const [sortDir, setSortDir] = useState("desc");

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const sorted = useMemo(() => {
    const mul = sortDir === "asc" ? 1 : -1;
    const byName = (a, b) =>
      `${a.employee.last} ${a.employee.first}`
        .toLowerCase()
        .localeCompare(
          `${b.employee.last} ${b.employee.first}`.toLowerCase(),
          "ru",
        );

    const compare = (a, b) => {
      if (sortField === "name") return mul * byName(a, b);
      let va;
      let vb;
      if (sortField === "norm") {
        va = a.norm;
        vb = b.norm;
      } else if (sortField === "fact") {
        va = a.fact;
        vb = b.fact;
      } else if (sortField === "deviation") {
        va = Math.abs(a.deviation);
        vb = Math.abs(b.deviation);
      } else if (sortField === "tasks") {
        va = a.tasks.length;
        vb = b.tasks.length;
      } else return byName(a, b);

      if (va < vb) return -1 * mul;
      if (va > vb) return 1 * mul;
      return byName(a, b);
    };

    return [...reminders].sort(compare);
  }, [reminders, sortField, sortDir]);

  const sortableHeader = (field, label, extraClass = "") => (
    <th
      className={`sortable${extraClass ? " " + extraClass : ""}`}
      onClick={() => handleSort(field)}
    >
      {label} {sortField === field && (sortDir === "asc" ? "↑" : "↓")}
    </th>
  );

  const resolvedPeriod = useMemo(
    () => resolveReminderPeriod(period, new Date(), from, to),
    [period, from, to],
  );
  const hasOverwork = sorted.some((r) => r.overwork > 0);

  if (loading) {
    return (
      <Modal title="Предпросмотр" onClose={onClose} width={760}>
        <div className="empty-note p-4">Загрузка…</div>
      </Modal>
    );
  }

  return (
    <Modal
      title="Предпросмотр: кому уйдут напоминания"
      onClose={onClose}
      width={760}
    >
      <p className="mut sm mb-2">
        {resolvedPeriod && (
          <>
            <b>Период:</b> {fmtDMY(resolvedPeriod.from)} –{" "}
            {fmtDMY(resolvedPeriod.to)}.{" "}
          </>
        )}
        Список получателей на текущий момент без отправки.
      </p>
      <div className="w-full overflow-x-auto">
        <table className="tbl">
          <thead>
            <tr>
              {sortableHeader("name", "Сотрудник")}
              {sortableHeader("norm", "Норма", "text-right")}
              {sortableHeader("fact", "Внесено", "text-right")}
              {sortableHeader("deviation", "Отклонение", "text-right")}
              {sortableHeader("tasks", "Задач", "text-right")}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr>
                <td colSpan="5" className="mut text-center p-5">
                  Получателей нет
                </td>
              </tr>
            )}
            {sorted.map((r) => (
              <tr key={r.employee.id}>
                <td>
                  <b>
                    {r.employee.last} {r.employee.first}
                  </b>
                </td>
                <td className="text-right">{r.norm} ч</td>
                <td className="text-right">{r.fact} ч</td>
                <td className="text-right">
                  <b className={r.overwork > 0 ? "wr-deviation--over" : ""}>
                    {formatDeviation(r.deviation)} ч
                  </b>
                  {r.overwork > 0 && (
                    <span
                      className="st-chip overwork ml-2"
                      title={WEEKLY_REMINDER_OVERWORK_HINT}
                    >
                      ⚠ {WEEKLY_REMINDER_OVERWORK_CHIP}
                    </span>
                  )}
                </td>
                <td className="text-right">{r.tasks.length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {hasOverwork && (
        <p className="mut sm mt-2">⚠ {WEEKLY_REMINDER_OVERWORK_LEGEND}.</p>
      )}
      <div className="modal-foot">
        <div className="spacer" />
        <button className="btn ghost" onClick={onClose}>
          Закрыть
        </button>
      </div>
    </Modal>
  );
}

export default function Cabinet({
  data,
  user,
  openTask,
  openProject,
  openVacation,
  openDelegation,
}) {
  const { capabilities } = useAuth();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const { empName, getTaskSpent } = useDataHelpers(data);
  const {
    commit: commitLog,
    praise,
    closePraise,
  } = useCommitTimeLog({ currentUser: user });

  const { data: vacationsRes } = useVacationsQuery();
  const { data: roleDelegationsRes } = useRoleDelegationsQuery();

  const vacations = useMemo(() => unwrapItems(vacationsRes), [vacationsRes]);
  const roleDelegations = useMemo(
    () => unwrapItems(roleDelegationsRes),
    [roleDelegationsRes],
  );

  const { data: wrSettings } = useWeeklyReminderSettingsQuery();
  const updateWrSettingsMutation = useUpdateWeeklyReminderSettingsMutation();
  const sendWrMutation = useSendWeeklyRemindersMutation();

  const updateSelfMutation = useUpdateSelfMutation();
  const uploadAvatarMutation = useUploadAvatarMutation();
  const deleteAvatarMutation = useDeleteAvatarMutation();
  const deleteVacationMutation = useDeleteVacationMutation();
  const revokeDelegationMutation = useRevokeRoleDelegationMutation();

  const [tab, setTab] = useState("overview");
  const [expFrom, setExpFrom] = useState("");
  const [expTo, setExpTo] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [extension, setExtension] = useState(user.extension || "");
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const [cellEdit, setCellEdit] = useState(null);
  const cellSavingRef = useRef(false);
  const fileInputRef = useRef(null);

  const canManageReminders = Boolean(capabilities.manageWeeklyReminders);
  const [wrEdits, setWrEdits] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [wrPeriod, setWrPeriod] = useState("prev_week");
  const [wrFrom, setWrFrom] = useState("");
  const [wrTo, setWrTo] = useState("");

  const wrDraft = wrEdits ?? normalizeWrDraft(wrSettings);

  const updateWrField = (field, value) => {
    setWrEdits((prev) => ({
      ...(prev ?? normalizeWrDraft(wrSettings)),
      [field]: value,
    }));
  };

  const handleWrSave = () => {
    updateWrSettingsMutation.mutate(wrDraft, {
      onSuccess: () => {
        setWrEdits(null);
        showToast(TOASTS.weeklyReminderSaved, "success");
      },
      onError: (err) =>
        showToast(err.message || "Не удалось сохранить настройки", "error"),
    });
  };

  const validateManualPeriod = () => {
    if (wrPeriod === "custom") {
      if (!wrFrom || !wrTo) {
        showToast(TOASTS.weeklyReminderPeriodRequired, "warning");
        return null;
      }
      if (wrFrom > wrTo) {
        showToast(TOASTS.weeklyReminderPeriodInvalid, "error");
        return null;
      }
      return { period: "custom", from: wrFrom, to: wrTo };
    }
    return { period: wrPeriod };
  };

  const handlePreviewOpen = () => {
    if (!validateManualPeriod()) return;
    setPreviewOpen(true);
  };

  const handleWrSendNow = async () => {
    const range = validateManualPeriod();
    if (!range) return;

    const ok = await confirm(DIALOGS.weeklyRemindersSendNow);
    if (!ok) return;

    sendWrMutation.mutate(
      { force: true, ...range },
      {
        onSuccess: (res) => {
          showToast(
            res.sent > 0
              ? TOASTS.weeklyRemindersSent(res.sent)
              : TOASTS.weeklyReminderNoRecipients,
            "success",
          );
        },
        onError: (err) => showToast(err.message || "Ошибка отправки", "error"),
      },
    );
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showToast("Пожалуйста, выберите изображение", "error");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast("Размер файла не должен превышать 5 МБ", "error");
      return;
    }

    uploadAvatarMutation.mutate(
      { id: user.id, file },
      {
        onSuccess: () => showToast("Фото профиля обновлено", "success"),
        onError: (err) =>
          showToast(err.message || "Не удалось загрузить фото", "error"),
      },
    );
    e.target.value = "";
  };

  const handleAvatarDelete = async () => {
    const ok = await confirm("Вы уверены, что хотите удалить фото профиля?");
    if (!ok) return;

    deleteAvatarMutation.mutate(user.id, {
      onSuccess: () => showToast("Фото профиля удалено", "success"),
      onError: (err) =>
        showToast(err.message || "Не удалось удалить фото", "error"),
    });
  };

  const positions = useMemo(
    () => buildEmployeePositions(user, data),
    [user, data],
  );

  const tasks = data.tasks;
  const projects = data.projects;

  const myTasks = useMemo(
    () =>
      tasks.filter(
        (t) => t.isActive && t.assigneeId === user.id && !user.fired,
      ),
    [tasks, user.id, user.fired],
  );

  const myProjects = useMemo(() => {
    const ids = [...new Set(myTasks.map((t) => t.projectId))];
    return ids.map((id) => projects.find((p) => p.id === id)).filter(Boolean);
  }, [myTasks, projects]);

  const myVacs = useMemo(
    () =>
      vacations.filter((v) => v.employeeId === user.id || v.empId === user.id),
    [vacations, user.id],
  );

  const myDeleg = useMemo(
    () =>
      roleDelegations.filter(
        (r) => r.permissions?.canManage || r.permissions?.canDecide,
      ),
    [roleDelegations],
  );

  const days = useMemo(() => {
    const out = [];
    for (let i = 19; i >= 0; i--) out.push(iso(addDays(new Date(), -i)));
    return out;
  }, []);

  const groupedTasks = useMemo(() => {
    const visibleTasks = myTasks.filter((t) => t.status !== "new");
    const groupMap = new Map();

    visibleTasks.forEach((t) => {
      const pid = t.projectId || "none";
      if (!groupMap.has(pid)) {
        const project = projects.find((p) => p.id === pid);
        groupMap.set(pid, {
          project: project || {
            id: "none",
            code: "-",
            name: "Без проекта",
            color: "var(--slate-300)",
          },
          tasks: [],
        });
      }
      const logMap = {};
      t.logs.forEach((l) => {
        logMap[l.date] = (logMap[l.date] || 0) + l.hours;
      });
      groupMap.get(pid).tasks.push({ task: t, logMap });
    });

    return Array.from(groupMap.values());
  }, [myTasks, projects]);

  const exportMyReport = () => {
    if (!expFrom || !expTo) {
      showToast("Выберите даты начала и окончания периода", "warning");
      return;
    }
    if (expFrom > expTo) {
      showToast("Дата начала не может быть позже даты окончания", "error");
      return;
    }
    const allLogs = [];
    myTasks.forEach((t) => {
      const project = projects.find((p) => p.id === t.projectId);
      t.logs.forEach((l) => {
        if (l.date >= expFrom && l.date <= expTo) {
          allLogs.push({
            date: l.date,
            hours: l.hours,
            task: t.title,
            project: project?.code || "-",
          });
        }
      });
    });
    if (!allLogs.length) {
      showToast("За выбранный период нет учтенных часов", "warning");
      return;
    }
    const rows = [["Проект", "Задача", "Дата", "Часы"]];
    allLogs.forEach((l) =>
      rows.push([l.project, l.task, fmtDMY(l.date), l.hours]),
    );
    downloadCsv(`отчет_${user.last}_${expFrom}_${expTo}`, rows);
    showToast("Отчёт выгружен!", "success");
  };

  const canEditCalendar = Boolean(capabilities.manageProductionCalendar);

  const tabs = [
    ["overview", "Сводка"],
    ["vacations", "Мои отпуска"],
    ["delegation", "Делегирование и замещение"],
    ["profile", "Профиль и уведомления"],
    ...(canEditCalendar ? [["calendar", "Правка календаря"]] : []),
  ];

  const handleSaveProfile = () => {
    if (!extension.trim()) {
      showToast("Внутренний номер телефона не может быть пустым", "error");
      return;
    }
    updateSelfMutation.mutate(
      { extension: extension.trim() },
      {
        onSuccess: () => {
          setEditMode(false);
          showToast("Данные обновлены", "success");
        },
        onError: (err) => {
          showToast(err.message || "Не удалось обновить данные", "error");
        },
      },
    );
  };

  const toggleNotif = (key) => {
    updateSelfMutation.mutate({
      notif: { ...user.notif, [key]: !user.notif[key] },
    });
  };

  const handleRevokeDelegation = (rd) => {
    revokeDelegationMutation.mutate(rd.id, {
      onSuccess: () => showToast("Замещение отозвано", "success"),
      onError: (err) =>
        showToast(err.message || "Не удалось отозвать замещение", "error"),
    });
  };

  const startCellEdit = (task, date) => {
    if (isLogBlockedByTaskStatus(task)) {
      showToast(STATUS_NEW_LOG_MESSAGE, "warning");
      return;
    }
    const existingLog = task.logs.find((l) => l.date === date);
    setCellEdit({
      taskId: task.id,
      date,
      logId: existingLog?.id || null,
      hours: existingLog ? String(existingLog.hours) : "",
      error: null,
    });
  };

  const cancelCellEdit = () => setCellEdit(null);

  const saveCellEdit = async () => {
    if (!cellEdit || cellSavingRef.current) return;
    cellSavingRef.current = true;
    const localEdit = cellEdit;
    try {
      const task = myTasks.find((t) => t.id === localEdit.taskId);
      if (!task) {
        setCellEdit(null);
        return;
      }
      const result = await commitLog({
        task,
        entry: {
          hours: localEdit.hours,
          date: localEdit.date,
          editingLogId: localEdit.logId,
        },
        getTaskSpent,
      });
      if (!result.ok) {
        if (result.reason === "validation") {
          showToast(result.error, "error");
          setCellEdit((prev) => ({ ...prev, error: result.error }));
        } else if (result.reason === "cancelled") {
          setCellEdit(null);
        }
        return;
      }
      setCellEdit(null);
      showToast(
        localEdit.logId ? "Запись обновлена" : "Часы учтены",
        "success",
      );
    } catch (err) {
      showToast(err.message || "Ошибка при сохранении часов", "error");
      setCellEdit(null);
    } finally {
      cellSavingRef.current = false;
    }
  };

  return (
    <div className="cab">
      <div className="tabs">
        {tabs.map(([id, label]) => (
          <button
            key={id}
            className={`tab${tab === id ? " on" : ""}`}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="flex flex-col gap-4">
          <div className="rep-panel">
            <div className="rep-panel-title">
              Затраченные часы по задачам (последние 20 дней)
            </div>
            <div className="toolbar flex items-center flex-wrap gap-2 mb-3">
              <span className="mut sm">Экспорт:</span>
              <input
                className="inp w-150"
                type="date"
                value={expFrom}
                onChange={(e) => setExpFrom(e.target.value)}
              />
              <span className="mut sm">-</span>
              <input
                className="inp w-150"
                type="date"
                value={expTo}
                onChange={(e) => setExpTo(e.target.value)}
              />
              <button className="btn primary sm" onClick={exportMyReport}>
                <Ic d={ICONS.download} size={13} /> Выгрузить в Excel (CSV)
              </button>
            </div>

            <div className="w-full overflow-x-auto">
              <table className="tbl w-full cab-timesheet-table">
                <thead>
                  <tr>
                    <th className="text-left border-b cab-timesheet-task-col">
                      Задача
                    </th>
                    {days.map((d) => (
                      <th
                        key={d}
                        className="text-center border-b text-xs text-mut cab-timesheet-day-col nowrap"
                      >
                        {fmtD(d)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groupedTasks.map((group) => (
                    <Fragment key={group.project.id}>
                      <tr className="cab-timesheet-project-row">
                        <td colSpan={days.length + 1}>
                          <div className="flex items-center gap-2">
                            <ColorDot
                              className="pdot"
                              color={group.project.color}
                            />
                            <span>
                              {group.project.code} - {group.project.name}
                            </span>
                          </div>
                        </td>
                      </tr>
                      {group.tasks.map(({ task, logMap }) => (
                        <tr key={task.id}>
                          <td className="text-left font-semibold border-b">
                            <button
                              type="button"
                              className="clickable-reset cab-timesheet-task-btn"
                              onClick={() => openTask(task.id)}
                              title="Открыть задачу"
                            >
                              {task.title}
                            </button>
                          </td>
                          {days.map((d) => {
                            const isEditing =
                              cellEdit &&
                              cellEdit.taskId === task.id &&
                              cellEdit.date === d;
                            return (
                              <td key={d} className="text-center border-b">
                                {isEditing ? (
                                  <input
                                    className={`inp cab-timesheet-cell-input${cellEdit.error ? " cab-timesheet-cell-input--error" : ""}`}
                                    type="number"
                                    min="0.5"
                                    step="0.5"
                                    value={cellEdit.hours}
                                    title={cellEdit.error || undefined}
                                    onChange={(e) =>
                                      setCellEdit({
                                        ...cellEdit,
                                        hours: e.target.value,
                                        error: null,
                                      })
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        saveCellEdit();
                                      } else if (e.key === "Escape") {
                                        e.preventDefault();
                                        cancelCellEdit();
                                      }
                                    }}
                                    autoFocus
                                  />
                                ) : (
                                  <button
                                    type="button"
                                    className={`cab-timesheet-cell-btn${logMap[d] ? " cab-timesheet-cell-btn--filled" : ""}`}
                                    onClick={() => startCellEdit(task, d)}
                                  >
                                    {logMap[d] ? `${logMap[d]} ч` : ""}
                                  </button>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </Fragment>
                  ))}
                  {groupedTasks.length === 0 && (
                    <tr>
                      <td
                        colSpan={days.length + 1}
                        className="mut text-center p-5"
                      >
                        Нет задач для отображения
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rep-panel">
            <div className="rep-panel-title">Мои задачи по статусам</div>
            {TASK_STATUS_ORDER.map((st) => {
              const list = myTasks.filter((t) => t.status === st);
              if (!list.length) return null;
              return (
                <div key={st} className="cab-stat">
                  <div className="cab-stat-head">
                    <ColorDot
                      className="kdot"
                      color={TASK_STATUSES[st].color}
                    />
                    {TASK_STATUSES[st].label}
                    <span className="kcount">{list.length}</span>
                  </div>
                  {list.map((t) => (
                    <Clickable
                      key={t.id}
                      className="cab-row clickable-reset"
                      onClick={() => openTask(t.id)}
                      title="Открыть задачу"
                    >
                      <ColorDot
                        className="pdot"
                        color={
                          projects.find((p) => p.id === t.projectId)?.color
                        }
                      />
                      {t.title}
                      <span className="mut sm">
                        {" · "}
                        {t.deadline
                          ? `до ${fmtD(t.deadline)}`
                          : "без срока исполнения"}
                        {" · "}
                        {getTaskSpent(t)}/{t.plannedHours ?? "-"} ч
                      </span>
                    </Clickable>
                  ))}
                </div>
              );
            })}
            {myTasks.length === 0 && <div className="mut">Задач пока нет</div>}
          </div>

          <div className="rep-panel">
            <div className="rep-panel-title">Проекты с моими задачами</div>
            {myProjects.map((p) => (
              <Clickable
                key={p.id}
                className="cab-row clickable-reset"
                onClick={() => openProject(p.id)}
                title="Открыть карточку проекта"
              >
                <ColorDot className="pdot" color={p.color} />
                {p.code} - {p.name}
                {p.ptype === "admin" && (
                  <span className="adm-badge ml-2">адм</span>
                )}
              </Clickable>
            ))}
            {myProjects.length === 0 && (
              <div className="mut">Нет участия в проектах</div>
            )}
          </div>
        </div>
      )}

      {tab === "vacations" && (
        <div className="rep-panel">
          <div className="rep-panel-title flex items-center">
            Мои отпуска
            <button
              className="btn primary sm ml-auto"
              onClick={() => openVacation(null)}
            >
              <Ic d={ICONS.plus} size={13} /> Добавить отпуск
            </button>
          </div>
          <table className="tbl">
            <thead>
              <tr>
                <th>Начало</th>
                <th>Окончание</th>
                <th>Тип</th>
                <th>Комментарий</th>
                <th>Статус</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {myVacs.map((v) => {
                const started = v.startDate <= TODAY || v.start <= TODAY;
                return (
                  <tr key={v.id}>
                    <td>{fmtDMY(v.startDate || v.start)}</td>
                    <td>{fmtDMY(v.endDate || v.end)}</td>
                    <td>{VACATION_TYPES[v.type]}</td>
                    <td>{v.comment || "-"}</td>
                    <td>
                      <span className={`st-chip ${v.status}`}>
                        {
                          {
                            pending: "На утверждении",
                            approved: "Утверждён",
                            rejected: "Отклонён",
                          }[v.status]
                        }
                      </span>
                    </td>
                    <td>
                      {!started && v.status !== "rejected" ? (
                        <>
                          <button
                            className="icon-btn"
                            onClick={() => openVacation(v.id)}
                          >
                            <Ic d={ICONS.edit} size={14} />
                          </button>
                          <button
                            className="icon-btn danger"
                            onClick={() =>
                              deleteVacationMutation.mutate(v.id, {
                                onSuccess: () =>
                                  showToast("Отпуск удалён", "success"),
                                onError: (err) =>
                                  showToast(
                                    err.message || "Не удалось удалить отпуск",
                                    "error",
                                  ),
                              })
                            }
                          >
                            <Ic d={ICONS.trash} size={14} />
                          </button>
                        </>
                      ) : (
                        <span className="mut sm">
                          {started ? "изменение через HR/администратора" : ""}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
              {myVacs.length === 0 && (
                <tr>
                  <td colSpan="6" className="mut">
                    Отпусков нет
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "delegation" && (
        <div className="rep-panel">
          <div className="rep-panel-title flex items-center">
            Временная передача ролей
            <button className="btn primary sm ml-auto" onClick={openDelegation}>
              <Ic d={ICONS.plus} size={13} /> Добавить
            </button>
          </div>
          <p className="mut sm">
            Роли «Суперадминистратор» и «Генеральный директор» делегируются
            только через суперадминистратора. Получатель должен подтвердить
            принятие. Здесь же - замещения на период отсутствия (отпуск,
            командировка, больничный), создаваемые вручную: с отпуском они
            больше не связаны.
          </p>
          <table className="tbl">
            <thead>
              <tr>
                <th>От кого</th>
                <th>Кому</th>
                <th>Роли</th>
                <th>Период</th>
                <th>Статус</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {myDeleg.map((r) => {
                const canManage = Boolean(r.permissions?.canManage);
                return (
                  <tr key={r.id}>
                    <td>{empName(r.fromId)}</td>
                    <td>{empName(r.toId)}</td>
                    <td>{r.roles.join(", ")}</td>
                    <td>
                      {fmtDMY(r.startDate || r.start)} -{" "}
                      {r.endDate || r.end
                        ? fmtDMY(r.endDate || r.end)
                        : "до отмены"}
                    </td>
                    <td>
                      <span className={`st-chip ${r.status}`}>
                        {{
                          pending: "Ожидает принятия",
                          active: "Активно",
                          rejected: "Отклонено",
                          revoked: "Отозвано",
                          cancelled: "Отменено",
                          expired: "Истекло",
                        }[r.status] || r.status}
                      </span>
                    </td>
                    <td>
                      {r.status === "active" && canManage && (
                        <button
                          className="btn ghost sm"
                          onClick={() => handleRevokeDelegation(r)}
                        >
                          Отозвать
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {myDeleg.length === 0 && (
                <tr>
                  <td colSpan="6" className="mut">
                    Делегирований нет
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {tab === "calendar" && canEditCalendar && <ProductionCalendarView />}

      {tab === "profile" && (
        <>
          <div className="cab-grid">
            <div className="rep-panel">
              <div className="rep-panel-title">Личные данные</div>
              <div className="flex gap-4 flex-wrap">
                <div className="cab-photo-col">
                  <div className="cab-photo-upload-wrapper">
                    <div className="cab-avatar-container">
                      <Avatar employee={user} size="xl" />
                      {user.photo && (
                        <button
                          type="button"
                          className="cab-avatar-delete-btn"
                          onClick={handleAvatarDelete}
                          disabled={
                            deleteAvatarMutation.isPending ||
                            uploadAvatarMutation.isPending
                          }
                          title="Удалить фото"
                        >
                          <Ic d={ICONS.close} size={14} />
                        </button>
                      )}
                      {uploadAvatarMutation.isPending && (
                        <div className="cab-photo-upload-overlay">
                          Загрузка…
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn primary sm cab-photo-upload-btn"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadAvatarMutation.isPending}
                    >
                      <Ic d={ICONS.edit} size={14} /> Добавить фото
                    </button>
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      className="hidden"
                      onChange={handleAvatarChange}
                    />
                  </div>
                </div>
                <div className="cab-profile-fields">
                  <div className="form-grid">
                    <label className="lbl">Фамилия</label>
                    <input className="inp" disabled value={user.last} />
                    <label className="lbl">Имя</label>
                    <input className="inp" disabled value={user.first} />
                    <label className="lbl">Должность</label>
                    <input
                      className="inp"
                      disabled
                      value={user.position || "Сотрудник"}
                    />
                    <label className="lbl">E-mail</label>
                    <input className="inp" disabled value={user.email || ""} />
                    <label className="lbl">
                      Внутренний номер телефона
                      <span className="required-star">*</span>
                    </label>
                    <input
                      className="inp"
                      disabled={!editMode}
                      value={extension}
                      onChange={(e) => setExtension(e.target.value)}
                      onBlur={() => {
                        if (!extension.trim())
                          showToast(
                            "Внутренний номер телефона обязателен",
                            "error",
                          );
                      }}
                    />
                    <label className="lbl">Табельный №</label>
                    <input className="inp" disabled value={user.tab || ""} />
                    <label className="lbl">Подразделения</label>
                    <div className="depts-readonly">
                      {positions.map((p, i) => (
                        <span
                          key={i}
                          className={`dept-chip${p.kind === "primary" ? " prim" : ""}`}
                        >
                          {formatPositionLabel(p)}
                          <span className="mut sm">
                            {" · "}
                            {p.kind === "primary" ? "основное" : "совм"}
                          </span>
                        </span>
                      ))}
                      {positions.length === 0 && (
                        <span className="mut sm">не назначены</span>
                      )}
                    </div>
                  </div>
                  <div className="mt-4 flex gap-3 justify-end items-center">
                    <button
                      className="btn danger"
                      onClick={() => setShowPasswordModal(true)}
                    >
                      Изменить пароль
                    </button>
                    {editMode ? (
                      <>
                        <button
                          className="btn ghost"
                          onClick={() => {
                            setEditMode(false);
                            setExtension(user.extension || "");
                          }}
                        >
                          Отмена
                        </button>
                        <button
                          className="btn primary"
                          onClick={handleSaveProfile}
                          disabled={updateSelfMutation.isPending}
                        >
                          {updateSelfMutation.isPending
                            ? "Сохранение…"
                            : "Сохранить"}
                        </button>
                      </>
                    ) : (
                      <button
                        className="btn primary"
                        onClick={() => setEditMode(true)}
                      >
                        Изменить данные
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="stack-col">
              <div className="rep-panel">
                <div className="rep-panel-title">Уведомления</div>
                {[
                  [
                    "deadlineEmail",
                    "E-mail о сроках - за 3 дня до срока исполнения задачи",
                  ],
                  ["overdueDigest", "Контроль просрочек - ежедневная сводка"],
                  [
                    "commentSub",
                    "Подписка на обсуждение задач, где я исполнитель или ответственный",
                  ],
                  [
                    "assignmentEmail",
                    "E-mail о запросах согласования назначений (вы — руководитель)",
                  ],
                ].map(([k, label]) => (
                  <label key={k} className="toggle-row">
                    <span>{label}</span>
                    <span
                      className={`toggle${user.notif[k] ? " on" : ""}`}
                      onClick={() => toggleNotif(k)}
                    >
                      <span className="toggle-knob" />
                    </span>
                  </label>
                ))}
                <p className="mut sm">
                  Критичные уведомления (восстановление пароля, утверждения)
                  отключить нельзя.
                </p>
                <div className="rep-panel-title mt-4">
                  История делегирований
                </div>
                {roleDelegations
                  .filter((r) => r.fromId === user.id)
                  .map((r) => (
                    <div key={r.id} className="mut sm">
                      → {empName(r.toId)}: {r.roles.join(", ")} (
                      {fmtDMY(r.startDate || r.start)} -{" "}
                      {r.endDate || r.end
                        ? fmtDMY(r.endDate || r.end)
                        : "до отмены"}
                      )
                    </div>
                  ))}
                {roleDelegations.filter((r) => r.fromId === user.id).length ===
                  0 && <div className="mut sm">Передач ролей не было</div>}
              </div>

              {canManageReminders && (
                <div className="rep-panel">
                  <div className="rep-panel-title">Напоминания о часах</div>
                  <div className="wr-section">
                    <div className="wr-section-title">
                      Автоматическое еженедельное напоминание
                    </div>
                    <label className="toggle-row">
                      <span>Включено</span>
                      <span
                        className={`toggle${wrDraft.enabled ? " on" : ""}`}
                        onClick={() =>
                          updateWrField("enabled", !wrDraft.enabled)
                        }
                      >
                        <span className="toggle-knob" />
                      </span>
                    </label>
                    <div className="form-grid mt-2">
                      <label className="lbl">День недели</label>
                      <Select
                        value={wrDraft.sendWeekday}
                        onChange={(v) =>
                          updateWrField("sendWeekday", Number(v))
                        }
                        options={WEEKDAY_OPTIONS}
                        autoSort={false}
                      />
                      <label className="lbl">Час (МСК)</label>
                      <Select
                        value={wrDraft.sendHour}
                        onChange={(v) => updateWrField("sendHour", Number(v))}
                        options={HOUR_OPTIONS}
                        autoSort={false}
                      />
                    </div>
                    <div className="mt-3 flex justify-end">
                      <button
                        type="button"
                        className="btn primary"
                        onClick={handleWrSave}
                      >
                        Сохранить
                      </button>
                    </div>
                    {wrSettings?.lastSentAt && (
                      <p className="mut sm mt-2">
                        Последняя автоотправка:{" "}
                        {fmtDMY(new Date(wrSettings.lastSentAt))}
                      </p>
                    )}
                  </div>
                  <div className="wr-section">
                    <div className="wr-section-title">
                      Разовое ручное напоминание
                    </div>
                    <div className="form-grid mt-2">
                      <label className="lbl">Период</label>
                      <Select
                        value={wrPeriod}
                        onChange={setWrPeriod}
                        options={WEEKLY_REMINDER_PERIOD_LIST}
                        autoSort={false}
                      />
                      {wrPeriod === "custom" && (
                        <>
                          <label className="lbl">С</label>
                          <input
                            className="inp"
                            type="date"
                            value={wrFrom}
                            onChange={(e) => setWrFrom(e.target.value)}
                          />
                          <label className="lbl">По</label>
                          <input
                            className="inp"
                            type="date"
                            value={wrTo}
                            onChange={(e) => setWrTo(e.target.value)}
                          />
                        </>
                      )}
                    </div>
                    <div className="mt-3 flex justify-end gap-2 flex-wrap">
                      <button
                        type="button"
                        className="btn ghost"
                        onClick={handlePreviewOpen}
                      >
                        Предпросмотр
                      </button>
                      <button
                        type="button"
                        className="btn danger"
                        onClick={handleWrSendNow}
                      >
                        Отправить сейчас
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showPasswordModal && (
            <ChangePasswordModal onClose={() => setShowPasswordModal(false)} />
          )}
          {previewOpen && canManageReminders && (
            <WeeklyReminderPreviewModal
              period={wrPeriod}
              from={wrFrom}
              to={wrTo}
              onClose={() => setPreviewOpen(false)}
            />
          )}
        </>
      )}

      {praise && (
        <DailyNormPraiseModal date={praise.date} onClose={closePraise} />
      )}
    </div>
  );
}
